import React, { SyntheticEvent, useCallback, useEffect, useRef } from 'react';
import { PasswordInputStrengthMeter } from '@/containers/pages/user/feature/create-user/components/sections/general-info/password/password-input';
import { Button, PasswordInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useStyles } from '../styles';
import useCopyToClipboard from '@hooks/useCopyToClipboard';
import { z } from 'zod';
import { useForm, zodResolver } from '@mantine/form';
import { generatePassword } from '@/utils/passwordUtils';
import { useDisclosure } from '@mantine/hooks';
import { IPasswordSecurity } from '@/entities/admin-app/filials';
import { usePasswordSchema } from '@hooks/password/useGeneratePolicy';
import { preserveCursorPosition } from '@/utils/preserveCursorPosition';
import { RulesConditionList } from '@/containers/pages/register/components/PassRulesList';
import { useGenerateRules } from '@hooks/password/useGenerateRules';
import { defaultMailServerPasswordOptions } from '@/mock-data/passwordOptions';

interface FormValues {
  Password: string;
  ConfirmPassword: string;
}

interface IFormProps {
  passwordRegexp: RegExp;
  passwordStrengthOptions?: IPasswordSecurity;
  handleUpdatePassword: (value: any) => void;
}

export const ResetPasswordForm = ({
  passwordRegexp,
  handleUpdatePassword,
  passwordStrengthOptions
}: IFormProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [visible, { toggle }] = useDisclosure(false);
  const { copy } = useCopyToClipboard();
  const passwordInputRef = useRef<HTMLInputElement | null>(null);

  const passwordRulesZ = usePasswordSchema(
    passwordStrengthOptions || defaultMailServerPasswordOptions,
    t
  );

  const schema = z
    .object({
      Password: passwordRulesZ,
      ConfirmPassword: z.string()
    })
    .refine((data) => !data?.ConfirmPassword?.length || data?.Password === data?.ConfirmPassword, {
      message: t('passwordReset.passwordsDoNotMatch'),
      path: ['ConfirmPassword']
    });

  const form = useForm<FormValues>({
    initialValues: { Password: '', ConfirmPassword: '' },
    validateInputOnChange: true,
    validate: zodResolver(schema)
  });

  const { Password, ConfirmPassword } = form.values;

  const onChange = useCallback(
    (event: SyntheticEvent<HTMLInputElement>) => {
      const type = event.currentTarget.dataset.value ?? '';
      const value = event.currentTarget.value.replace(/\s+/g, '');
      switch (type) {
        case 'password':
          form.setFieldValue('Password', value);
          break;
        case 'confirm-password':
          form.setFieldValue('ConfirmPassword', value);
          break;

        default:
          break;
      }
      form.validate();
    },
    [Password, ConfirmPassword, passwordRegexp]
  );

  const password = Password;

  const rules = useGenerateRules(password, passwordStrengthOptions);
  const summary = `${t('register.passwordAllowedStart')} ${rules
    .map((i) => i.shortLabel)
    .join(', ')}.`;

  const handleSubmit = async () => {
    const values = form.values;
    handleUpdatePassword(values);
  };

  const handleGeneratePassword = () => {
    const pass = generatePassword(passwordRegexp, passwordStrengthOptions?.MinLength);
    if (pass && passwordRegexp.test(pass)) {
      form.setFieldValue('Password', pass);
      form.setFieldValue('ConfirmPassword', pass);
    }
    copy(pass);
  };

  useEffect(() => {
    if (Password.length) {
      if (passwordRegexp.test(Password)) {
        form.clearFieldError('Password');
      }
    }
  }, [Password, passwordRegexp]);

  useEffect(() => {
    const validatePasswordMatch = () => {
      if (ConfirmPassword.length) {
        if (Password === ConfirmPassword) {
          form.clearFieldError('ConfirmPassword');
        } else {
          form.setFieldError('ConfirmPassword', t('passwordReset.passwordsDoNotMatch'));
        }
      } else {
        form.clearFieldError('ConfirmPassword');
      }
    };

    validatePasswordMatch();
  }, [Password, ConfirmPassword]);

  useEffect(() => {
    if (passwordRegexp && Password) {
      form.validateField('Password');
    }
  }, [passwordRegexp, Password]);

  const fixCursorLocation = () => {
    preserveCursorPosition(passwordInputRef, toggle);
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <RulesConditionList rules={rules} check={password.length > 0} />
      <PasswordInputStrengthMeter
        rules={rules}
        summary={summary}
        size="lg"
        radius="md"
        variant="filled"
        placeholder={t('passwordReset.newPassword')}
        label=""
        value={form.getInputProps('Password').value}
        onChange={onChange}
        handleGeneratePassword={handleGeneratePassword}
        error={form.getInputProps('Password').error}
      />
      <PasswordInput
        size="lg"
        variant="filled"
        placeholder={t('passwordReset.confirmNewPassword')}
        radius="md"
        data-value="confirm-password"
        label=""
        withAsterisk={true}
        visible={visible}
        onVisibilityChange={fixCursorLocation}
        onChange={onChange}
        ref={passwordInputRef}
        value={form.getInputProps('ConfirmPassword').value}
        error={form.getInputProps('ConfirmPassword').error}
        className={classes.input}
      />

      <Button
        size="lg"
        disabled={!form.isValid() || Password !== ConfirmPassword}
        type="submit"
        fullWidth
        mt="xl"
      >
        {t('save')}
      </Button>
    </form>
  );
};
